// components/BannerPreview/index.js
import React from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  FormControl,
  Select,
  MenuItem,
  Switch,
  IconButton,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import MDBox from 'components/MDBox';
import { BlueButton } from 'styles';

const bannerPositionStyles = {
  'bottom-left': {
    bottom: '16px',
    left: '16px',
    right: '16px',
    width: 'calc(100% - 32px)',
    flexDirection: 'column',
  },
  'bottom-right': {
    bottom: '16px',
    left: '16px',
    right: '16px',
    width: 'calc(100% - 32px)',
    flexDirection: 'column',
  },
  left: {
    bottom: '16px',
    left: '16px',
    width: '420px',
    maxWidth: 'calc(100% - 32px)',
    flexDirection: 'column',
    borderRadius: '12px',
    justifyContent: 'flex-start',
  },
  right: {
    bottom: '16px',
    right: '16px',
    width: '420px',
    maxWidth: 'calc(100% - 32px)',
    flexDirection: 'column',
    borderRadius: '12px',
    justifyContent: 'flex-start',
  },
};

const preferencesPositionStyles = {
  left: {
    top: '300px',
    height: '520px',
    left: '1px',
    transform: 'translateY(-50%)',
    width: '383px',
    flexDirection: 'column',
    borderRadius: '0 12px 12px 0', // top-right & bottom-right only
    justifyContent: 'left',
  },
  right: {
    top: '300px',
    height: '520px',
    right: '1px',
    transform: 'translateY(-50%)',
    width: '383px',
    flexDirection: 'column',
    borderRadius: '12px 0 0 12px', // top-right & bottom-right only
    justifyContent: 'left',
  },
};

const BannerPreview = ({
  themeSettings,
  currentLanguageSettings,
  currentDomainSettings,
  cookies,
  activeLang,
  previewTabValue,
  openModal,
  cookiePreferences,
  expandedCategories,
  availableLangs,
  onLangChange,
  onPreviewTabChange,
  onCustomize,
  onAcceptAll,
  onRejectAll,
  onAcceptSelection,
  onPreferenceChange,
  onCategoryToggle,
  onSnackbar,
}) => {
  // Only one accordion open at a time in preferences pop-up
  const [accordionOpen, setAccordionOpen] = React.useState('pref');
  if (!themeSettings || !currentLanguageSettings || !currentDomainSettings) {
    return null;
  }

  const themeColors = themeSettings.themeColors || {};
  const bannerType = themeSettings.type || 'banner';
  const rawShow = themeSettings?.showCookieDetails;
  const showDetails = rawShow == null ? true : String(rawShow).toLowerCase() === 'true';
  const unknownLabel = activeLang === 'tr' ? 'Bilinmiyor' : 'Unknown';
  const noDescriptionLabel = activeLang === 'tr' ? 'Açıklama yok' : 'No description';
  const rejectLabel = activeLang === 'tr' ? 'Reddet' : 'Reject';
  const customizeLabel = activeLang === 'tr' ? 'Tercihleri Seç' : 'Customize';
  const acceptLabel = activeLang === 'tr' ? 'Tümünü Kabul Et' : 'Accept All';
  const savePreferencesLabel = activeLang === 'tr' ? 'Tercihleri Kaydet' : 'Save Preferences';
  const getCookieSource = (cookie) =>
    cookie?.domain || cookie?.observedDomain || cookie?.source || unknownLabel;

  const renderCookieMetadata = (cookie) => (
    <MDBox display="flex" flexDirection="column" alignItems="flex-start" gap={0.25}>
      <Typography variant="caption" sx={{ color: 'black !important', overflowWrap: 'anywhere' }}>
        <span style={{ fontWeight: 'bold' }}>{activeLang === 'tr' ? 'Süre: ' : 'Duration: '}</span>
        {cookie.duration || unknownLabel}
      </Typography>
      <Typography variant="caption" sx={{ color: 'black !important', overflowWrap: 'anywhere' }}>
        <span style={{ fontWeight: 'bold' }}>{activeLang === 'tr' ? 'Kaynak: ' : 'Source: '}</span>
        {getCookieSource(cookie)}
      </Typography>
    </MDBox>
  );

  const lightenColor = (color, percent) => {
    if (!color || color === 'transparent') return '#ffffff';
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, (num >> 16) + amt);
    const G = Math.min(255, ((num >> 8) & 0x00ff) + amt);
    const B = Math.min(255, (num & 0x0000ff) + amt);
    return `#${((1 << 24) | (R << 16) | (G << 8) | B).toString(16).slice(1)}`;
  };

  const getAbsoluteUrl = (url) => {
    if (!url) return '#';
    if (!url.match(/^https?:\/\//)) {
      return `https://${url}`;
    }
    return url;
  };

  const extractAndRenderLinks = (text) => {
    if (!text || !currentLanguageSettings?.bannerTexts?.cookiePolicyLinkText) return text;

    const cleanPolicyText = currentLanguageSettings?.bannerTexts?.cookiePolicyLinkText.replace(/\[\[|\]\]/g, '');

    if (!text.includes(`[[${cleanPolicyText}]]`)) {
      return text;
    }

  const linkRegex = /\[\[(.*?)\]\]/g;
  const matches = [];
  let lastIndex = 0;
  const elements = [];
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    matches.push({
      text: match[1],
      start: match.index,
      end: match.index + match[0].length,
    });
  }

    if (matches.length === 0) return text;

    matches.forEach((m, index) => {
      if (m.start > lastIndex) {
        elements.push(<span key={`text-${index}`}>{text.substring(lastIndex, m.start)}</span>);
      }

      if (m.text === cleanPolicyText) {
        elements.push(
          <a
            key={`link-${index}`}
            href={getAbsoluteUrl(currentLanguageSettings?.cookiePolicyUrl)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: themeColors.textColor,
              textDecoration: 'underline',
              fontWeight: 700,
              cursor: 'pointer',
            }}
            onClick={() => {
              if (onSnackbar) {
                onSnackbar(activeLang === 'tr' ? 'Link kopyalandı!' : 'Link copied!', 'success');
              }
            }}
          >
            {m.text}
          </a>
        );
      } else {
        elements.push(<span key={`plain-${index}`}>{`[[${m.text}]]`}</span>);
      }

      lastIndex = m.end;
    });

    if (lastIndex < text.length) {
      elements.push(<span key="text-end">{text.substring(lastIndex)}</span>);
    }

    return elements;
  };

  const renderLanguageSelector = () => (
    <FormControl size="small" sx={{ minWidth: 60 }}>
      <Select
        value={activeLang}
        onChange={onLangChange}
        IconComponent={(props) => <KeyboardArrowDownIcon {...props} fontSize="medium" />}
        sx={{
          paddingY: '5px',
          backgroundColor: themeColors.backgroundColor,
          color: themeColors.textColor,
          '& .MuiSelect-select': {
            backgroundColor: themeColors.backgroundColor,
            color: themeColors.textColor,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: themeColors.textColor,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: themeColors.textColor,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: themeColors.textColor,
          },
          '& .MuiSvgIcon-root': {
            color: themeColors.textColor,
          },
          '& .MuiSelect-icon': {
            display: 'block !important',
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: `${lightenColor(themeColors.backgroundColor, 7)} !important`,
              color: themeColors.textColor,
              '& .MuiMenuItem-root': {
                color: themeColors.textColor,
                '&:hover': {
                  backgroundColor: themeColors.hoverColor || 'rgba(255, 255, 255, 0.1)',
                  color: themeColors.textColor,
                },
                '&.Mui-selected': {
                  backgroundColor: themeColors.selectedColor || 'rgba(255, 255, 255, 0.2)',
                  color: themeColors.textColor,
                },
              },
            },
          },
        }}
      >
        {availableLangs && availableLangs.map((code) => (
          <MenuItem key={code} value={code}>
            {code.toUpperCase()}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderCategoryCard = (category) => {
    const list = cookies?.[currentDomainSettings?.id]?.[activeLang]?.[category.category];
    const hasCookies = Array.isArray(list) && list.length > 0;
    
    // showDetails true ise ve çerez yoksa, kartı gösterme
    if (showDetails && !hasCookies) return null;

    return (
      <MDBox
        key={category.category}
        sx={{
          p: 1.5,
          borderRadius: '10px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: themeColors.cardBackground || '#f8f9fac4',
          color: themeColors.textColor,
          transition: 'all 0.3s ease',
          mb: 1,
        }}
      >
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: '100%',
            cursor: showDetails && hasCookies ? 'pointer' : 'default',
          }}
          onClick={showDetails && hasCookies ? () => onCategoryToggle(category.category) : undefined}
        >
          {showDetails && hasCookies && (
            <IconButton>
              {expandedCategories[category.category] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}

          <MDBox sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {category.name}{' '}
              {showDetails && hasCookies && (
                <span style={{ fontWeight: 'normal' }}>
                  ({list.length} {activeLang === 'tr' ? 'çerez' : list.length === 1 ? 'cookie' : 'cookies'})
                </span>
              )}
            </Typography>
            <Typography variant="caption" paragraph mt={0.5}>
              {category.description}
            </Typography>
          </MDBox>

          <Switch
            checked={cookiePreferences[category.category] || false}
            onChange={onPreferenceChange(category.category)}
            disabled={category.category === 'necessary'}
            color="primary"
            sx={{
              ml: 2,
              '& .MuiSwitch-thumb': { boxShadow: 1 },
              '& .MuiSwitch-track': { opacity: 0.7 },
              '&.Mui-disabled': { opacity: 0.5 },
            }}
          />
        </MDBox>

        {showDetails && hasCookies && (
          <Collapse in={expandedCategories[category.category]} sx={{ width: '100%' }}>
            <MDBox sx={{ p: 0, bgcolor: 'transparent' }}>
              {list.map((cookie) => (
                <Box
                  key={cookie.id}
                  sx={{
                    p: 1.5,
                    mt: 1,
                    mb: 1,
                    backgroundColor: '#eeeeee',
                    borderRadius: '10px',
                    '& *': { color: 'black !important' },
                  }}
                >
                  <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', color: 'black !important' }}>
                    {cookie.name}
                  </Typography>
                  <Typography variant="caption" paragraph sx={{ color: 'black !important' }}>
                    {cookie.description || noDescriptionLabel}
                  </Typography>
                  {renderCookieMetadata(cookie)}
                </Box>
              ))}
            </MDBox>
          </Collapse>
        )}
      </MDBox>
    );
  };

  const renderDialogBanner = () => (
    <MDBox
      sx={{
        position: 'absolute',
        top: '5vh',
        left: '50%',
        transform: 'translateX(-50%)',
        width: { xs: '95%', md: '700px' },
        height: '450px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: themeColors.backgroundColor,
        color: themeColors.textColor,
        borderRadius: '12px',
        boxShadow: 3,
        p: 2,
        zIndex: 0,
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-thumb': { background: themeColors.textColor, opacity: 0.2, borderRadius: '3px' },
      }}
    >
      <Tabs
        value={previewTabValue}
        onChange={onPreviewTabChange}
        variant="fullWidth"
        sx={{
          mb: 3,
          backgroundColor: themeColors.backgroundColor,
          // Hide default MUI indicator (uses transform -> glitches under parent scale)
          '& .MuiTabs-indicator': {
            display: 'none',
          },
          // Add base pseudo-element for all tabs for animated underline
          '& .MuiTab-root::after': {
            content: '""',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: 2,
            height: '3px',
            width: 0,
            borderRadius: '3px',
            backgroundColor: 'transparent',
            transition: 'width .2s ease, background-color .2s ease',
          },
          // Updated Tab styles: larger & bolder titles
          '& .MuiTab-root': {
            color: themeColors.textColor,
            fontSize: '0.95rem',          // was 0.8rem → a bit larger
            fontWeight: 600,               // default a bit bolder
            position: 'relative',
            paddingBottom: '12px',         // a touch more breathing room
            '&.Mui-selected': {
              color: themeColors.buttonBgColor,
              fontWeight: 700,             // selected even bolder
            },
            // Custom crisp underline that survives parent scaling
            '&.Mui-selected::after': {
              content: '""',
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: 2,
              height: '3px',
              width: '160px',
              borderRadius: '3px',
              backgroundColor: themeColors.textColor,
            },
            '&:hover': {
              color: themeColors.textColor + ' !important',
            },
            '&.Mui-disabled': {
              color: themeColors.textColor + ' !important',
            },
          },
        }}
      >
        <Tab
          label={currentLanguageSettings?.dialogHeadings?.dialogGeneralHeading || (activeLang === 'tr' ? 'Genel' : 'General')}
          sx={{ pb: 1, color: themeColors.textColor + ' !important' }}
        />
        <Tab
          label={currentLanguageSettings?.dialogHeadings?.dialogCookiesHeading || (activeLang === 'tr' ? 'Çerezler' : 'Cookies')}
          sx={{ pb: 1, color: themeColors.textColor + ' !important' }}
        />
        <Tab
          label={currentLanguageSettings?.dialogHeadings?.dialogDetailsHeading || (activeLang === 'tr' ? 'Detaylar' : 'Details')}
          sx={{ pb: 1, color: themeColors.textColor + ' !important' }}
        />
      </Tabs>

      {/* Scrollable tab panels */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          pr: 1,
          pb: 1,
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {previewTabValue === 0 && (
          <>
            <Box sx={{ mb: 2, textAlign: 'left', px: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                {currentLanguageSettings?.logoUrl && (
                  <img src={currentLanguageSettings?.logoUrl} alt="Logo" style={{ width: 'auto', height: '20px' }} />
                )}
                {renderLanguageSelector()}
              </Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: themeColors.textColor }}>
                {currentLanguageSettings?.bannerTexts?.bannerTitle}
              </Typography>
              <Typography variant="caption" whiteSpace="pre-line" paragraph sx={{ fontSize: '0.875rem', color: themeColors.textColor }} component="div">
                {extractAndRenderLinks(currentLanguageSettings?.bannerTexts?.bannerDescription)}
              </Typography>
            </Box>
          </>
        )}

        {previewTabValue === 1 && (
          <MDBox sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 2 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: themeColors.textColor }}>
              {currentLanguageSettings?.preferenceTexts?.preferencesTitle || (activeLang === 'tr' ? 'Çerez Tercihleri' : 'Cookie Preferences')}
            </Typography>
            <Typography variant="caption" whiteSpace="pre-line" paragraph mb={2} sx={{ color: themeColors.textColor }}>
              {extractAndRenderLinks(currentLanguageSettings?.preferenceTexts?.preferencesDescription)}
            </Typography>
            {currentLanguageSettings?.categories?.map(renderCategoryCard)}
          </MDBox>
        )}

        {previewTabValue === 2 && (
          <Box sx={{ textAlign: 'left', px: 2 }}>
            {currentLanguageSettings?.informationalTexts?.moreInfoText?.trim() && (
              <Typography
                variant="caption"
                whiteSpace="pre-line"
                paragraph
                sx={{ color: themeColors.textColor, fontSize: '0.95rem', lineHeight: 1.6 }}
              >
                {extractAndRenderLinks(currentLanguageSettings?.informationalTexts?.moreInfoText)}
              </Typography>
            )}
            {currentLanguageSettings?.informationalTexts?.privacyIntroText?.trim() && (
              <Typography
                variant="caption"
                whiteSpace="pre-line"
                paragraph
                sx={{ color: themeColors.textColor, fontSize: '0.95rem', lineHeight: 1.6 }}
              >
                {extractAndRenderLinks(currentLanguageSettings?.informationalTexts?.privacyIntroText)}
              </Typography>
            )}

            {!showDetails && (
              <Accordion sx={{ backgroundColor: themeColors.backgroundColor, color: themeColors.textColor, borderRadius: 1, mt: 2, boxShadow: 'none' }}>
                <AccordionSummary
                  sx={{ px: 0, '& .MuiAccordionSummary-content': { margin: 0 } }}
                  expandIcon={<ExpandMoreIcon sx={{ color: themeColors.textColor }} />}
                >
                  <Typography variant="h6" fontWeight="bold" sx={{ color: themeColors.textColor }}>
                    {activeLang === 'tr' ? 'Kullandığımız Çerezler' : 'Cookies We Use'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {Object.values(cookies?.[currentDomainSettings?.id]?.[activeLang] || {})
                    .flat()
                    .map((cookie) => (
                      <Box key={cookie.id} sx={{ p: 1.5, mt: 1, mb: 2, backgroundColor: '#eeeeee', borderRadius: '10px', '& *': { color: 'black !important' } }}>
                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', color: 'black !important' }}>
                          {cookie.name}
                        </Typography>
                        <Typography variant="caption" paragraph sx={{ color: 'black !important' }}>
                          {cookie.description || noDescriptionLabel}
                        </Typography>
                        {renderCookieMetadata(cookie)}
                      </Box>
                    ))}
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}
      </Box>

      {/* Sticky footer buttons – varies per tab */}
      {previewTabValue === 0 && (
        <MDBox
          sx={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: themeColors.backgroundColor,
            zIndex: 2,
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            flexWrap: 'wrap',
            mt: 0,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <BlueButton onClick={onRejectAll} buttontext={themeColors.buttonTextColor} buttonbackground={themeColors.buttonBgColor}>
            {currentLanguageSettings?.bannerTexts?.rejectText || rejectLabel}
          </BlueButton>
          <BlueButton onClick={() => onPreviewTabChange(null, 1)} buttontext={themeColors.buttonTextColor} buttonbackground={themeColors.buttonBgColor}>
            {currentLanguageSettings?.bannerTexts?.customizeText || customizeLabel}
          </BlueButton>
          <BlueButton onClick={onAcceptAll} buttontext={themeColors.buttonTextColor} buttonbackground={themeColors.buttonBgColor}>
            {currentLanguageSettings?.bannerTexts?.acceptText || acceptLabel}
          </BlueButton>
        </MDBox>
      )}

      {previewTabValue === 1 && (
        <MDBox
          sx={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: themeColors.backgroundColor,
            zIndex: 2,
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            flexWrap: 'wrap',
            mt: 0,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <BlueButton onClick={onAcceptSelection} buttontext={themeColors.buttonTextColor} buttonbackground={themeColors.buttonBgColor}>
            {currentLanguageSettings?.preferenceTexts?.savePreferencesText || savePreferencesLabel}
          </BlueButton>
        </MDBox>
      )}
      {/* For Details tab (index 2) no buttons */}
    </MDBox>
  );

  const renderBarBanner = () => (
    <Box
      sx={{
        position: 'absolute',
        backgroundColor: themeColors.backgroundColor,
        color: themeColors.textColor,
        boxShadow: 3,
        p: 3,
        pb: openModal ? 0 : 3,
        zIndex: 0,
        overflowY: 'auto',
        display: 'flex',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        ...(!openModal ? bannerPositionStyles[themeSettings.position] : {}),
        ...(openModal ? preferencesPositionStyles[themeSettings.preferencesPosition] : {}),
      }}
    >
      {!openModal ? (
        <Box sx={{ width: '100%', height: '100%', display: 'contents' }}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              {currentLanguageSettings?.logoUrl && (
                <img src={currentLanguageSettings?.logoUrl} alt="Logo" style={{ width: 'auto', height: '20px' }} />
              )}
              {renderLanguageSelector()}
            </Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: themeColors.textColor }}>
              {currentLanguageSettings?.bannerTexts?.bannerTitle}
            </Typography>
            <Typography variant="caption" whiteSpace="pre-line" paragraph sx={{ fontSize: '0.875rem', color: themeColors.textColor }} component="div">
              {extractAndRenderLinks(currentLanguageSettings?.bannerTexts?.bannerDescription) ||
                'Web sitemizde en iyi deneyimi yaşamanızı sağlamak için çerezler kullanıyoruz.'}
            </Typography>
          </Box>

          <MDBox
            sx={{
              display: 'flex',
              gap: 2,
              marginTop: 'auto',
              flexWrap: 'nowrap',
              ...(themeSettings.position === 'bottom-left' && { justifyContent: 'flex-start' }),
              ...(themeSettings.position === 'bottom-right' && { justifyContent: 'flex-end' }),
              ...(themeSettings.position === 'left' && { justifyContent: 'center' }),
              ...(themeSettings.position === 'right' && { justifyContent: 'center' }),
            }}
          >
            <BlueButton onClick={onRejectAll} buttontext={themeColors.buttonTextColor} buttonbackground={themeColors.buttonBgColor}>
              {currentLanguageSettings?.bannerTexts?.rejectText || rejectLabel}
            </BlueButton>
            <BlueButton onClick={onCustomize} buttontext={themeColors.buttonTextColor} buttonbackground={themeColors.buttonBgColor}>
              {currentLanguageSettings?.bannerTexts?.customizeText || customizeLabel}
            </BlueButton>
            <BlueButton onClick={onAcceptAll} buttontext={themeColors.buttonTextColor} buttonbackground={themeColors.buttonBgColor}>
              {currentLanguageSettings?.bannerTexts?.acceptText || acceptLabel}
            </BlueButton>
          </MDBox>
        </Box>
      ) : (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ position: 'relative', minHeight: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
            <IconButton onClick={onCustomize} aria-label="close" sx={{ position: 'absolute', top: -24, right: -18, zIndex: 3, color: themeColors.textColor }}>
              <CloseIcon />
            </IconButton>

            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                pr: 1,
                pb: 0,
                display: 'flex',
                flexDirection: 'column',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                // Only draw top border and spacing between accordion items
                '& .MuiAccordion-root + .MuiAccordion-root': {
                  marginTop: 1,
                  paddingTop: 1,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                },
              }}
            >
              <Accordion
                disableGutters
                expanded={accordionOpen === 'pref'}
                onChange={(_, isExpanded) => setAccordionOpen(isExpanded ? 'pref' : false)}
                sx={{
                  backgroundColor: themeColors.backgroundColor,
                  color: themeColors.textColor,
                  borderRadius: 1,
                  boxShadow: 'none',
                  // remove MUI's default top divider pseudo-element
                  '&:before': { display: 'none' },
                  // keep summary text from shifting when expanded
                  '& .MuiAccordionSummary-content': { margin: 0 },
                  '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },
                }}
              >
                <AccordionSummary
                  sx={{ px: 0, minHeight: 0, '&.Mui-expanded': { minHeight: 0 } }}
                  expandIcon={<ExpandMoreIcon sx={{ color: themeColors.textColor }} />}
                >
                  <Typography variant="h6" fontWeight="bold" sx={{ color: themeColors.textColor }}>
                    {currentLanguageSettings?.preferenceTexts?.preferencesTitle || (activeLang === 'tr' ? 'Çerez Tercihleri' : 'Cookie Preferences')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {currentLanguageSettings?.preferenceTexts?.preferencesDescription && (
                    <Typography variant="caption" whiteSpace="pre-line" paragraph sx={{ color: themeColors.textColor, mb: 2 }}>
                      {extractAndRenderLinks(currentLanguageSettings?.preferenceTexts?.preferencesDescription)}
                    </Typography>
                  )}
                  {currentLanguageSettings?.categories?.map(renderCategoryCard)}
                </AccordionDetails>
              </Accordion>

              {(currentLanguageSettings?.informationalTexts?.moreInfoText?.trim() || currentLanguageSettings?.informationalTexts?.privacyIntroText?.trim()) && (
                <Accordion
                  disableGutters
                  expanded={accordionOpen === 'info'}
                  onChange={(_, isExpanded) => setAccordionOpen(isExpanded ? 'info' : false)}
                  sx={{
                    backgroundColor: themeColors.backgroundColor,
                    color: themeColors.textColor,
                    borderRadius: 1,
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    '& .MuiAccordionSummary-content': { margin: 0 },
                    '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },
                  }}
                >
                  <AccordionSummary
                    sx={{ px: 0, minHeight: 0, '&.Mui-expanded': { minHeight: 0 } }}
                    expandIcon={<ExpandMoreIcon sx={{ color: themeColors.textColor }} />}
                  >
                    <Typography variant="h6" fontWeight="bold" sx={{ color: themeColors.textColor }}>
                      {currentLanguageSettings?.dialogHeadings?.dialogDetailsHeading || (activeLang === 'tr' ? 'Bilgilendirme' : 'Information')}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {currentLanguageSettings?.informationalTexts?.moreInfoText?.trim() && (
                      <Typography variant="caption" whiteSpace="pre-line" paragraph sx={{ color: themeColors.textColor }}>
                        {extractAndRenderLinks(currentLanguageSettings?.informationalTexts?.moreInfoText)}
                      </Typography>
                    )}
                    {currentLanguageSettings?.informationalTexts?.privacyIntroText?.trim() && (
                      <Typography variant="caption" whiteSpace="pre-line" paragraph sx={{ color: themeColors.textColor }}>
                        {extractAndRenderLinks(currentLanguageSettings?.informationalTexts?.privacyIntroText)}
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              )}

              {!showDetails && (
                <Accordion
                  disableGutters
                  expanded={accordionOpen === 'cookies'}
                  onChange={(_, isExpanded) => setAccordionOpen(isExpanded ? 'cookies' : false)}
                  sx={{
                    backgroundColor: themeColors.backgroundColor,
                    color: themeColors.textColor,
                    borderRadius: 1,
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    '& .MuiAccordionSummary-content': { margin: 0 },
                    '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },
                  }}
                >
                  <AccordionSummary
                    sx={{ px: 0, minHeight: 0, '&.Mui-expanded': { minHeight: 0 } }}
                    expandIcon={<ExpandMoreIcon sx={{ color: themeColors.textColor }} />}
                  >
                    <Typography variant="h6" fontWeight="bold" sx={{ color: themeColors.textColor }}>
                      {activeLang === 'tr' ? 'Kullandığımız Çerezler' : 'Cookies We Use'}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {Object.values(cookies?.[currentDomainSettings?.id]?.[activeLang] || {})
                      .flat()
                      .map((cookie) => (
                        <Box key={cookie.id} sx={{ p: 1.5, mt: 1, mb: 2, backgroundColor: '#eeeeee', borderRadius: '10px', '& *': { color: 'black !important' } }}>
                          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', color: 'black !important' }}>
                            {cookie.name}
                          </Typography>
                          <Typography variant="caption" paragraph sx={{ color: 'black !important' }}>
                            {cookie.description || noDescriptionLabel}
                          </Typography>
                          {renderCookieMetadata(cookie)}
                        </Box>
                      ))}
                  </AccordionDetails>
                </Accordion>
              )}

              <Box
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  width: '100%',
                  mt: 'auto',
                  py: 2,
                  backgroundColor: themeColors.backgroundColor,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  zIndex: 2,
                }}
              >
                <BlueButton fullWidth onClick={onAcceptSelection} buttontext={themeColors.buttonTextColor} buttonbackground={themeColors.buttonBgColor}>
                  {currentLanguageSettings?.preferenceTexts?.savePreferencesText || savePreferencesLabel}
                </BlueButton>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );

  return bannerType === 'dialog' ? renderDialogBanner() : renderBarBanner();
};

export default BannerPreview;
